import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box } from '@material-ui/core';
import { colors } from '../../../theme/color-palette';
import { BOGLE_MEDIUM } from '../../../constants/constant';
import ComparisonChart from '../../../common/components/charts/ComparisonChart';
import UpwardsDarkIcon from '../../../assets/SVGs/UpwardsDarkGreen.svg';
import DownwardsDarkIcon from '../../../assets/SVGs/DownwardsDarkRed.svg';
import UpwardsIcon from '../../../assets/SVGs/UpwardsIcon.svg';
import DownwardsIcon from '../../../assets/SVGs/DownwardsIcon.svg';
import { useSelector } from 'react-redux';
import ErrorScreen from '../ErrorScreen';
import DownloadSupplierShare from '../../Download/DownloadSupplierShare';
import TextWithDefinition from '../../TextWithDefinition';
import PropTypes from 'prop-types';
import { Divider, Stack, ThemeProvider } from '@mui/material';
import { theme } from '../../../theme/theme';

const {
	PURPLE,
	// LIGHT_GREY,
	FONT_COLOR,
	// POSITIVE_GROWTH_COLOR,
	NEGATIVE_GROWTH_COLOR,
	SUCCESS_MD,
	ALERT_BORDER,
} = colors;

// Styles
const useStyles = makeStyles((theme) => ({
	container: {
		width: (P) => P.chartWidth,
		// border: `1px solid ${LIGHT_GREY}`,
		borderRadius: '8px',
		height: '422px',
	},
	loaderContainer: {
		height: '422px',
		display: 'flex',
	},
	circularProgress: {
		margin: 'auto',
	},
	saleChartHeader: {
		width: (P) => P.chartWidth,
		height: '63px',
		padding: '12px 16px',
		display: 'flex',
		flexDirection: 'column',
		gap: '8px',
	},
	percent: {
		fontSize: '20px',
		[theme.breakpoints.down('sm')]: {
			fontSize: '12px',
		},
		fontFamily: 'Bogle-Light',
		lineHeight: '27.8px',
		fontWeight: '300',
	},
	up: {
		color: SUCCESS_MD,
		lineHeight: '28px',
		fontWeight: 300,
		fontSize: '20px',
	},
	down: {
		color: NEGATIVE_GROWTH_COLOR,
		lineHeight: '28px',
		fontWeight: 300,
		fontSize: '20px',
	},
	arrow: {
		fontSize: 'x-large',
		marginBottom: '-3px',
		padding: '0 0 0 5px',
	},
	yoy: {
		fontSize: '12px',
		fontFamily: 'Bogle-Light',
		color: FONT_COLOR,
		marginLeft: '2px',
	},
	dashboard: {
		display: 'flex',
	},
}));

const styles = {
	saleChartTitle: {
		textAlign: 'left',
		fontSize: '18px',
		color: FONT_COLOR,
		fontFamily: 'Bogle-Regular',
	},
	saleChartAmount: {
		textAlign: 'left',
		fontSize: '24px',
		color: FONT_COLOR,
		lineHeight: '32px',
		fontWeight: 400,
		fontFamily: BOGLE_MEDIUM,
	},
};

/* Net sale chart component */
const SupplierShareChart = (props) => {
	const chartWidth = '100%';

	const classes = useStyles({
		chartWidth,
	});

	const chartData = useSelector(
		(state) => state?.anomalyDetectionReducer?.supplierShare
	);

	const selectedFilters = props.selectedFilters;

	const current_period =
		selectedFilters?.weekFilter >= 0
			? `WMT Last ${selectedFilters?.weekFilter} weeks`
			: 'Custom';

	const parseData = (data) => {
		let details = data?.details || [];
		if (data !== null) {
			details = details?.map((item) => {
				return {
					...item,
					current_percentage: item?.current_percentage / 100,
					previous_percentage: item?.previous_percentage / 100,
					ly_current_percentage: item.ly_current_percentage / 100,
					current_percentage_tooltip: item?.current_percentage,
					previous_percentage_tooltip: item?.previous_percentage,
					ly_current_percentage_tooltip: item.ly_current_percentage,
					excel_ly_current_week: `Week ${item.ly_current_week}`,
					currentWeek: `Week ${item?.current_week}`,
					previousWeek: `Week ${item?.previous_week}`,
					bpsChgRefArrow:
						item?.bps_change_ref > 0 ? UpwardsDarkIcon : DownwardsDarkIcon,
					bpsChgRefColor: item?.bps_change_ref > 0 ? '#113601' : '#590B0E',
					bpsChgRefBackgroundColor:
						item?.bps_change_ref > 0 ? '#EAF3E6' : '#F8D2D3',
					bpsChgLyArrow:
						item?.bps_change_ly > 0 ? UpwardsDarkIcon : DownwardsDarkIcon,
					bpsChgLyColor: item?.bps_change_ly > 0 ? '#113601' : '#590B0E',
					bpsChgLyBackgroundColor:
						item?.bps_change_ly > 0 ? '#EAF3E6' : '#F8D2D3',
					bps_change_ly:
						item?.bps_change_ly < 0
							? item?.bps_change_ly?.toString()?.slice(1)
							: item?.bps_change_ly,
					bps_change_ref:
						item?.bps_change_ref < 0
							? item?.bps_change_ref?.toString()?.slice(1)
							: item?.bps_change_ref,
				};
			});
			return { ...data, details: details };
		}
		return [];
	};

	const xAxisData = {
		nonDate: 'currentWeek',
	};

	const chartValue = [
		{
			value: 'ly_current_percentage',
			name: 'Last year',
			chartLine: '6,3',
			color: PURPLE,
		},

		{
			value: 'previous_percentage',
			name: 'Previous period',
			chartLine: '0',
			color: ALERT_BORDER,
		},
		{
			value: 'current_percentage',
			name: `${current_period}`,
			chartLine: '0',
			color: PURPLE,
		},
	];

	const getChartContent = () => {
		if (!chartData?.details) {
			return (
				<ErrorScreen
					chartTitle={'Supplier Dollar Share'}
					flag={chartData?.hierarchy_validation || 0}
					hasDefinition={true}
					definitionId={'supplierShare'}
				/>
			);
		} else {
			return (
				<>
					<Box className={classes.dashboard} data-test-id="Supplier Share">
						<Box className={classes.saleChartHeader}>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
								}}>
								<TextWithDefinition
									text={'Supplier Dollar Share'}
									styles={{
										...styles.saleChartTitle,
										color: colors.DARK_GREY,
									}}
									definitionId={'supplierShare'}
									hasDefinition={true}
								/>
								<Box
									sx={{
										margin: 0,
										padding: 0,
									}}>
									<DownloadSupplierShare
										data={parseData(chartData)}
										chartTitle={'Supplier Dollar Share'}
									/>
								</Box>
							</Box>

							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
								}}>
								<Box>
									<Typography style={{ ...styles.saleChartAmount }}>
										{chartData?.value + '%'}
									</Typography>
								</Box>
								{/* <Box> */}
								<Stack
									direction="row"
									divider={
										<Divider
											sx={{ paddingLeft: '4px', paddingRight: '4px' }}
											orientation="vertical"
											flexItem
										/>
									}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											padding: '0 4px',
										}}>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												padding: '0 4px',
												fontWeight: 300,
											}}>
											<Typography>
												{chartData?.ref_bps_change >= 0 ? (
													<>
														<img
															alt="up arrow"
															style={{
																height: '12px',
																width: '8px',
																padding: '2px 4px',
															}}
															src={UpwardsIcon}
															className={`${classes.up} ${classes.arrow}`}
														/>
														<span className={`${classes.up}`}>
															{`${chartData?.ref_bps_change}` + ' bps'}
														</span>
													</>
												) : (
													<>
														<img
															alt="down arrow"
															style={{
																height: '12px',
																width: '8px',
																padding: '2px 4px',
															}}
															src={DownwardsIcon}
															className={`${classes.down} ${classes.arrow}`}
														/>
														<span className={`${classes.down}`}>
															{`${Math.abs(chartData?.ref_bps_change)}` +
																' bps'}
														</span>
													</>
												)}
											</Typography>
										</Box>

										<Box
											sx={{
												fontSize: '12px',
												lineHeight: '16px',
												color: colors.GREY,
												paddingRight: '8px',
											}}>
											<Typography>vs previous period</Typography>
										</Box>
									</Box>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											padding: '0 4px',
										}}>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												padding: '0 4px',
												fontWeight: 700,
											}}>
											<Typography variant="bold">
												{chartData?.ly_bps_change >= 0 ? (
													<>
														<img
															alt="up arrow"
															style={{
																height: '12px',
																width: '8px',
																padding: '2px 4px',
															}}
															src={UpwardsIcon}
															className={`${classes.up} ${classes.arrow}`}
														/>
														<span className={`${classes.up}`}>
															{`${chartData?.ly_bps_change}` + ' bps'}
														</span>
													</>
												) : (
													<>
														<img
															alt="down arrow"
															style={{
																height: '12px',
																width: '8px',
																padding: '2px 4px',
															}}
															src={DownwardsIcon}
															className={`${classes.down} ${classes.arrow}`}
														/>
														<span className={`${classes.down}`}>
															{`${Math.abs(chartData?.ly_bps_change)}` + ' bps'}
														</span>
													</>
												)}
											</Typography>
										</Box>

										<Box
											sx={{
												fontSize: '12px',
												lineHeight: '16px',
												color: colors.GREY,
											}}>
											<Typography>vs last year</Typography>
										</Box>
									</Box>
								</Stack>
								{/* </Box> */}
							</Box>
						</Box>
					</Box>
					<ComparisonChart
						chartName={'LineChart'}
						chartData={parseData(chartData)}
						chartValue={chartValue}
						xAxisData={xAxisData}
						yAxisMinValue={0}
						chartTitle={'Supplier Dollar Share'}
						chartWidth={'100%'}
						chartHeight={'326px'}
					/>
				</>
			);
		}
	};
	return (
		<ThemeProvider theme={theme}>
			<Box className={classes.container} key={'Supplier Share'}>
				{getChartContent()}
			</Box>
		</ThemeProvider>
	);
};

SupplierShareChart.propTypes = {
	selectedFilters: PropTypes.object,
};

export default React.memo(SupplierShareChart);
